/**
 * Sstv2MigrationReservationFailure entity (DEV-193).
 *
 * A reservation that did NOT make it across a V1→V2 migration, recorded as its
 * own Thing so the SA migration page can answer "which reservations failed, and
 * why" without anyone reading CloudWatch traces.
 *
 * Two distinct failure sources are folded into one list, discriminated by
 * `source` — they fail in completely different places and were previously
 * invisible in different ways:
 *
 *   - **partner** — the reservation appeared in a `migration:reservations:*:done`
 *     payload's `failure_list`. The EVENT processed fine; the partner is telling
 *     us it could not migrate these. Before this entity the ids were dropped on
 *     the floor: the ingest handler took `failureList` as an argument and never
 *     read it, `received` counted only `successList`, and the sole surviving
 *     signal was a `failureCount` number on a trace field.
 *
 *   - **processing** — our own `materializeMigratedReservation` threw for a
 *     mapping in `success_list`. That bumped `activeFailed`/`historyFailed`, so
 *     the COUNT was visible on the migration record, but never which ids.
 *
 * Records are keyed per (tenant, sl1Locker, sl1Reservation, source), so a retry
 * that fails the same way overwrites rather than duplicating, and a reservation
 * that fails partner-side AND processing-side keeps both rows.
 */

export const Sstv2MigrationFailureThingTypeMrn =
  "mrn:sl2:::sstv2MigrationReservationFailure";

/** Where the failure was reported. See the module doc for the distinction. */
export const Sstv2MigrationFailureSourceList = [
  "partner",
  "processing",
] as const;
export type Sstv2MigrationFailureSource =
  (typeof Sstv2MigrationFailureSourceList)[number];

/** Which half of the migration the failure belongs to. */
export const Sstv2MigrationFailureStreamList = ["active", "history"] as const;
export type Sstv2MigrationFailureStream =
  (typeof Sstv2MigrationFailureStreamList)[number];

/**
 * Cap on how much of the partner's raw failure entry we persist. The entries are
 * small objects (`{sl1_reservation_id, sl2_reservation_id}`), but the partner
 * controls the shape — truncate rather than risk a 400 KB DDB item.
 */
export const SSTV2_FAILURE_RAW_ENTRY_MAX_CHARS = 2000;

/**
 * `migration:reservations:{active,inactive,history}:done` → stream.
 * The partner uses `inactive` and `history` interchangeably for the non-active
 * half depending on the event; both map to "history" on our side.
 */
export const sstv2FailureStreamForEvent = (
  eventType: string,
): Sstv2MigrationFailureStream =>
  eventType.includes(":active:") ? "active" : "history";

export const isSstv2MigrationFailureSource = (
  s: unknown,
): s is Sstv2MigrationFailureSource =>
  typeof s === "string" &&
  (Sstv2MigrationFailureSourceList as readonly string[]).includes(s);
